'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';

const YEMEN_EMBLEM = 'https://media.base44.com/images/public/69ed0fe31d3c9ede3b821281/406079c5f_Emblem_of_Yemen_2svg.png';

const navLinks = [
  { href: '/', label: 'الرئيسية' },
  { href: '/services', label: 'الخدمات' },
  { href: '/track', label: 'تتبع الطلب' },
  { href: '/faq', label: 'الأسئلة الشائعة' },
  { href: '/contact', label: 'تواصل معنا' },
];

interface NavbarProps {
  activePage?: string;
}

export default function Navbar({ activePage = '/' }: NavbarProps) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      {/* Top Government Bar */}
      <div className="bg-primary text-white text-xs py-1.5 px-4 hidden md:block">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <span className="text-white/70">الجمهورية اليمنية — وزارة الخارجية وشؤون المغتربين</span>
          <div className="flex items-center gap-4">
            <span className="text-white/70">الخط الساخن: 967+ 2 234 567</span>
            <span className="text-white/50">|</span>
            <span className="text-white/70">info@mofa.gov.ye</span>
          </div>
        </div>
      </div>

      {/* Main Navbar */}
      <nav className={`sticky top-0 z-50 font-tajawal w-full transition-all duration-300 ${
        scrolled
          ? 'bg-white/95 backdrop-blur-xl shadow-gov border-b border-border'
          : 'bg-white border-b border-border'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link href="/" className="flex items-center gap-3 shrink-0">
              <div className="w-12 h-12 flex items-center justify-center">
                <img
                  src={YEMEN_EMBLEM}
                  alt="شعار الجمهورية اليمنية"
                  className="w-12 h-12 object-contain"
                />
              </div>
              <div className="hidden sm:block">
                <div className="text-[10px] font-semibold text-muted-foreground leading-tight">الجمهورية اليمنية</div>
                <div className="text-sm font-bold text-primary leading-tight">وزارة الخارجية وشؤون المغتربين</div>
                <div className="text-[10px] text-accent font-semibold leading-tight">منصة تصريح</div>
              </div>
            </Link>

            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className={activePage === link.href ? 'nav-link-active' : 'nav-link'}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* Actions */}
            <div className="flex items-center gap-1 sm:gap-2">
              <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-muted hover:bg-secondary transition-colors border border-border text-xs font-medium text-foreground">
                <span className="text-base">🇺🇸</span>
                <span className="hidden sm:inline text-muted-foreground">English</span>
              </button>
              <Link href="/login" className="hidden md:flex">
                <button className="px-3 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors">
                  تسجيل الدخول
                </button>
              </Link>
              <Link href="/signup" className="hidden md:flex">
                <button className="px-3 py-1.5 text-xs font-medium border border-primary text-primary hover:bg-primary hover:text-white rounded-lg transition-colors">
                  تسجيل
                </button>
              </Link>
              <Link href="/apply" className="hidden sm:flex">
                <button className="px-4 py-1.5 text-xs font-semibold bg-accent text-white hover:bg-accent-dark rounded-lg transition-colors shadow-gov">
                  تقديم طلب ←
                </button>
              </Link>
              {/* Mobile Menu Button */}
              <button
                onClick={() => setMobileOpen(!mobileOpen)}
                className="lg:hidden p-2 rounded-lg bg-primary text-white hover:bg-primary-dark transition-colors"
                aria-label="القائمة"
              >
                {mobileOpen ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="lg:hidden bg-white border-t border-border shadow-gov-lg">
            <div className="max-w-7xl mx-auto px-4 py-3 space-y-1">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  onClick={() => setMobileOpen(false)}
                  className={`block px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    activePage === link.href
                      ? 'bg-primary/10 text-primary font-semibold' :'text-foreground hover:bg-muted'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <div className="pt-2 border-t border-border flex gap-2">
                <Link href="/login" className="flex-1">
                  <button className="w-full py-2 text-sm font-medium border border-border rounded-lg text-foreground hover:bg-muted transition-colors">
                    تسجيل الدخول
                  </button>
                </Link>
                <Link href="/apply" className="flex-1">
                  <button className="w-full py-2 text-sm font-semibold bg-accent text-white rounded-lg hover:bg-accent-dark transition-colors">
                    تقديم طلب
                  </button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </nav>
    </>
  );
}
